import React from "react";

// Shared card-table look for list pages (Manage User, Manage Role) — same design as the fleet User List

const pad2 = (n) => String(n).padStart(2, "0");

// "08-10-2026, 03:20 PM" — accepts epoch ms or a date string; "" when empty/invalid
export const formatDateTime = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const d = new Date(/^\d+$/.test(String(value)) ? Number(value) : value);
  if (isNaN(d.getTime())) return "";
  const h = d.getHours();
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}, ${pad2(h % 12 || 12)}:${pad2(d.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
};

export const listStyles = {
  page: { padding: "16px 20px", fontFamily: "'Segoe UI', sans-serif" },
  card: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    background: "linear-gradient(135deg, #0f4a45, #1a7a73)",
    padding: "12px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  td: { padding: "10px 14px", color: "#475569", fontSize: 12 },
  primaryBtn: {
    padding: "9px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
    background: "linear-gradient(135deg, #0f4a45, #1a7a73)", color: "#fff", boxShadow: "0 2px 8px rgba(26,122,115,0.3)",
  },
  tag: { background: "#f0fdfb", color: "#0f4a45", padding: "3px 9px", borderRadius: 6, fontWeight: 700 },
  empty: { padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 },
};

export const pill = (on, onText, offText) => (
  <span style={{ background: on ? "#dcfce7" : "#f1f5f9", color: on ? "#16a34a" : "#64748b", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
    {on ? onText : offText}
  </span>
);

const iconBtnStyle = (border, bg, color, disabled) => ({
  width: 30, height: 30, borderRadius: 7, border: `1px solid ${border}`, background: bg, color,
  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
  display: "flex", alignItems: "center", justifyContent: "center",
});

export const ViewButton = ({ onClick, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} title="View" style={iconBtnStyle("#dbeafe", "#eff6ff", "#2563eb", disabled)}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  </button>
);

export const EditButton = ({ onClick, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} title="Edit" style={iconBtnStyle("#ccfbf1", "#f0fdfb", "#0f4a45", disabled)}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  </button>
);

export const DeleteButton = ({ onClick, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} title="Delete" style={iconBtnStyle("#fecaca", "#fef2f2", "#ef4444", disabled)}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  </button>
);

// Card header: title + count badge + search box
export const ListCardHeader = ({ title, count, searchText, onSearch, placeholder }) => (
  <div style={listStyles.cardHeader}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{title}</span>
      <span style={{ background: "rgba(255,255,255,0.25)", color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
        {count}
      </span>
    </div>
    <input
      value={searchText}
      onChange={(e) => onSearch(e.target.value)}
      placeholder={placeholder}
      style={{ padding: "6px 12px", borderRadius: 7, border: "none", fontSize: 12, width: 280, outline: "none" }}
    />
  </div>
);

export const PAGE_SIZES = [10, 25, 50, 100];

const pagerBtn = (disabled) => ({
  minWidth: 32, height: 32, borderRadius: 7, border: "1px solid #e2e8f0",
  background: disabled ? "#f8fafc" : "#fff", color: disabled ? "#cbd5e1" : "#0f4a45",
  cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700, fontSize: 14,
});

// "Showing X to Y of N Entries" + page size + ‹ page/total ›
export const ListPager = ({ total, page, pageSize, onPage, onPageSize }) => {
  if (total === 0) return null;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, padding: "12px 18px", borderTop: "1px solid #f1f5f9" }}>
      <span style={{ fontSize: 12.5, color: "#1a7a73" }}>
        Showing {start + 1} to {Math.min(start + pageSize, total)} of {total} Entries
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          style={{ height: 32, borderRadius: 7, border: "1px solid #e2e8f0", padding: "0 8px", fontSize: 12.5, color: "#1e293b", background: "#fff" }}
        >
          {PAGE_SIZES.map((n) => <option key={n} value={n}>Show {n}</option>)}
        </select>
        <button type="button" style={pagerBtn(page <= 1)} disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
        <span style={{ fontSize: 12.5, color: "#0f4a45", fontWeight: 700, minWidth: 64, textAlign: "center" }}>
          {page} / {totalPages}
        </span>
        <button type="button" style={pagerBtn(page >= totalPages)} disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      </div>
    </div>
  );
};

// Light popup showing a long text in full (description, permission list…)
export const TextPopup = ({ title, items, onClose }) => (
  <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
      <div style={{ ...listStyles.cardHeader, padding: "12px 18px" }}>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{title}</span>
        <button type="button" onClick={onClose} title="Close"
          style={{ width: 30, height: 30, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.35)", background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 17, cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>
      <div style={{ padding: "16px 20px", overflowY: "auto" }}>
        {Array.isArray(items) ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {items.map((it, i) => (
              <span key={i} style={{ background: "#f0fdfb", color: "#0f4a45", border: "1px solid #ccfbf1", borderRadius: 6, padding: "3px 9px", fontSize: 12, fontWeight: 600 }}>{it}</span>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13.5, color: "#1e293b", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{items}</p>
        )}
      </div>
    </div>
  </div>
);
